import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { 
    OverviewTab, 
    UnusedMediaTab,
    DuplicatesTab,
    SettingsTab,
    TrashTab,
    OrphanedFilesTab,
    ScanHistoryTab,
    StockImagesTab
} from './components';

const AdminDashboard = () => {
    // Determine active tab from WordPress page parameter
    const getActiveTabFromUrl = () => {
        const page = window.mtrimAdminData?.currentPage || 'mtrim-dashboard';
        const pageMap = {
            'mtrim-dashboard': 'overview',
            'mtrim-unused': 'unused',
            'mtrim-duplicates': 'duplicates',
            'mtrim-orphaned': 'orphaned',
            'mtrim-stock': 'stock-images',
            'mtrim-trash': 'trash',
            'mtrim-settings': 'settings'
        };
        return pageMap[page] || 'overview';
    };

    const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());
    const [stats, setStats] = useState(window.mtrimAdminData?.stats || { total: 0, unused: 0, duplicates: 0 });

    const refreshStats = async () => {
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'mtrim_get_stats',
                    nonce: window.mtrimAdminData.nonce,
                })
            });

            const data = await response.json();
            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error refreshing stats:', error);
        }
    };

    const tabs = [
        { name: 'overview', title: __('Dashboard', 'media-trim'), icon: 'dashboard', page: 'mtrim-dashboard' },
        { name: 'unused', title: __('Unused Media', 'media-trim'), icon: 'media-code', page: 'mtrim-unused' },
        { name: 'duplicates', title: __('Duplicates', 'media-trim'), icon: 'images-alt2', page: 'mtrim-duplicates' },
        { name: 'orphaned', title: __('Orphaned Files', 'media-trim'), icon: 'media-document', page: 'mtrim-orphaned' },
        { name: 'stock-images', title: __('Stock Images', 'media-trim'), icon: 'format-image', page: 'mtrim-stock' },
        { name: 'trash', title: __('Trash', 'media-trim'), icon: 'trash', page: 'mtrim-trash' },
        { name: 'settings', title: __('Settings', 'media-trim'), icon: 'admin-settings', page: 'mtrim-settings' },
    ];

    // Handle tab change via page navigation
    const handleTabChange = (page) => {
        window.location.href = `admin.php?page=${page}`;
    };

    const renderTab = () => {
        switch (activeTab) {
            case 'overview':
                return <OverviewTab onSetTab={handleTabChange} stats={stats} refreshStats={refreshStats} />;
            case 'unused':
                return <UnusedMediaTab refreshStats={refreshStats} />;
            case 'duplicates':
                return <DuplicatesTab refreshStats={refreshStats} />;
            case 'orphaned':
                return <OrphanedFilesTab refreshStats={refreshStats} />;
            case 'stock-images':
                return <StockImagesTab refreshStats={refreshStats} />;
            case 'trash':
                return <TrashTab />;
            case 'settings':
                return <SettingsTab />;
            default:
                return <div>{__('Page not found', 'media-trim')}</div>;
        }
    };

    return (
        <div className="mtrim-dashboard-wrapper">
            {/* Top Announcement Banner */}

            <header className="mtrim-dashboard-header">
                <div className="mtrim-header-left">
                    <div className="mtrim-logo">
                        <svg width="374" height="120" viewBox="0 0 374 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g transform="translate(0, 5)">
                                <circle cx="55" cy="55" r="54" fill="#2e6da4" />
                                <circle cx="55" cy="55" r="49" stroke="white" strokeWidth="3" />

                                <path d="M32 45C32 42.2386 34.2386 40 37 40H50L53 43H75C77.7614 43 80 45.2386 80 48V72C80 74.7614 77.7614 77 75 77H37C34.2386 77 32 74.7614 32 72V45Z" fill="white" fillOpacity="0.15" stroke="white" strokeWidth="2.5" />
                                <path d="M38 72L50 60L60 70L70 63L78 72V74H38V72Z" fill="white" />
                                <circle cx="68" cy="52" r="4.5" fill="white" />

                                <rect x="22" y="58" width="66" height="4" rx="2" fill="#00D084" />

                                <path d="M75 28L77.5 33L82.5 34L77.5 35L75 40L72.5 35L67.5 34L72.5 33L75 28Z" fill="white" />
                                <path d="M86 38L87 41L90 42L87 43L86 46L85 43L82 42L85 41L86 38Z" fill="white" fillOpacity="0.8" />
                            </g>

                            <text x="125" y="68" fontFamily="Segoe UI, Arial, sans-serif" fontWeight="800" fontSize="38" fill="#2e6da4" style={{ letterSpacing: '1px' }}>MEDIA TRIM</text>
                            <text x="127" y="98" fontFamily="Georgia, serif" fontStyle="italic" fontSize="17" fill="#475569">Scan Fast &amp; Clean Safely</text>
                        </svg>
                    </div>
                </div>

                <div className="mtrim-header-center">
                    <nav className="mtrim-header-nav">
                        {tabs.map(tab => (
                            <button
                                key={tab.name}
                                className={`mtrim-nav-item ${activeTab === tab.name ? 'active' : ''}`}
                                onClick={() => handleTabChange(tab.page)}
                            >
                                <span className={`dashicons dashicons-${tab.icon}`}></span>
                                {tab.title}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="mtrim-header-right">
                    <div className="mtrim-version-badge">
                        {__('Version:', 'media-trim')} <span>1.0.2</span>
                    </div>
                </div>
            </header>

            <div className="wrap mtrim-dashboard">
                <div className="mtrim-content full-width">
                    {renderTab()}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;